import React, { useState, useEffect } from 'react';
import './Searchbar.css';
import { Link, useNavigate } from 'react-router-dom';
import { useDebounce } from '../../hooks/useDebounce'; // Adjust the path accordingly

const Searchbar = () => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState(null);
    const [isFocused, setIsFocused] = useState(false); // Track input focus
    const debouncedQuery = useDebounce(query, 300);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const navigate = useNavigate();

    useEffect(() => {
        if (!isFocused || debouncedQuery === '' || debouncedQuery.length <= 3) {
            setSuggestions(null);
            setSelectedIndex(-1);
            return;
        }
        // Fetch suggestions with debouncedQuery
        fetchSuggestions(debouncedQuery);
    }, [debouncedQuery, isFocused]);

    const fetchSuggestions = (searchQuery) => {
        // Fetch the suggestions from the backend
        
        fetch('/api/search/suggestion', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ searchQuery }),
        })
            .then((res) => res.json())
            .then((data) => {
                const searchResults = data;
                setSuggestions([...searchResults]);
            })
            .catch((err) => console.error(err));
    };

    const handleChange = (event) => {
        setQuery(event.target.value);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (selectedIndex !== -1) { return; }
        window.location.href = `/search?search_query=${encodeURIComponent(query)}`;
    };

    const handleFocus = () => {
        setIsFocused(true);
        setSuggestions(null); // Clear suggestions when refocusing
    };

    const handleBlur = () => {
        setTimeout(() => {
            setIsFocused(false);
            setSuggestions(null);
            setSelectedIndex(-1);
        }, 200);
    };

    const handleSuggestionClick = () => {
        setSuggestions(null); // Clear suggestions after navigation
        setQuery("");
        setSelectedIndex(-1);
    };

    const handleKeyDown = (event) => {
        if (!suggestions || suggestions.length === 0) return;

        if (event.key === 'ArrowDown') {
            // Move selection down
            setSelectedIndex((prevIndex) => {
                const newIndex = prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0;
                scrollToSuggestion(newIndex);
                return newIndex;
            });
        } else if (event.key === 'ArrowUp') {
            // Move selection up
            setSelectedIndex((prevIndex) => {
                const newIndex = prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1;
                scrollToSuggestion(newIndex);
                return newIndex;
            });
        } else if (event.key === 'Enter' && selectedIndex >= 0) {
            // Redirect to the selected suggestion
            const selectedSuggestion = suggestions[selectedIndex];
            const link = `/home/?${selectedSuggestion.topicId != null 
                    ? `node=${selectedSuggestion.topicId}`
                    : `path=${selectedSuggestion.pathId}`
                }`;
            navigate(link);
            setSuggestions(null); // Clear suggestions
            setQuery(''); // Clear query
        }
    };

    const scrollToSuggestion = (index) => {
        const suggestionList = document.querySelector('.suggestions');
        const suggestionItem = suggestionList?.children[index];

        if (suggestionItem) {
            suggestionItem.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    };

    return (
        <form className="searchbar" onSubmit={handleSubmit}>
            <span className="icon textbox-icon-container">
                <ion-icon name="search">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="icon icon-tabler icon-tabler-search"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        strokeWidth="1"
                        stroke="#555"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <circle cx="10" cy="10" r="7" />
                        <line x1="21" y1="21" x2="15" y2="15" />
                    </svg>
                </ion-icon>
            </span>
            <input
                type="text"
                placeholder="Search..."
                value={query}
                onChange={handleChange}
                onFocus={handleFocus} // Set isFocused to true and clear suggestions
                onBlur={handleBlur} // Set isFocused to false
                onKeyDown={handleKeyDown}
            />
            {suggestions && isFocused && query.length > 3 && (
                <ul className="suggestions">
                    {suggestions?.length > 0 ? (
                        suggestions.map((suggestion, index) => (
                            <Link
                                key={index}
                                to={`/home/?${suggestion.topicId != null ? `node=${suggestion.topicId}` : `path=${suggestion.pathId}`}`}
                                onClick={handleSuggestionClick} // Clear suggestions after navigation
                                className={index === selectedIndex ? 'suggestion-selected' : ''}
                            >
                                <li
                                    key={index}
                                >
                                    {suggestion.name || suggestion.pathName}
                                    {suggestion.pathId != null && (
                                        <span className="suggestions-tag">Path</span>
                                    )}
                                </li>
                            </Link>
                        ))
                    ) : (
                        <li className="no-results" > No results found</li>                        
                    )}
                </ul>
            )}
            <i className="fas fa-search"></i>
        </form>
    );
};

export default Searchbar;
